// transactionsDataGrid.columns.tsx
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-pro'
import { yellow800 } from 'ext-ui/style/colors'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { approvalActionsCellColDef } from 'src/components/DataGrid/cells/ApprovalActionsCell/ApprovalActionsCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { hoverActionsCellColDef } from 'src/components/DataGrid/cells/HoverActionsCell/HoverActionsCell'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import TextAndPillCell from 'src/components/DataGrid/cells/TextAndPillCell/TextAndPillCell'
import ManagerReviewIconCell from 'src/components/MangerReviewIconCell/MangerReviewIconCell'
import ReceiptPushStatusTooltip from 'src/components/ReceiptPushStatusTooltip/ReceiptPushStatusTooltip'
import ReceiptsTooltip from 'src/components/ReceiptsTooltip/ReceiptsTooltip'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import SingleReceiptReminder from 'src/components/SingleReceiptReminder/SingleReceiptReminder'
import SvgCheckNew from 'src/components/Svgs/SvgCheckNew'
import SvgClock from 'src/components/Svgs/SvgClock'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import TransactionPushStatusTooltip from 'src/components/TransactionPushStatusTooltip/TransactionPushStatusTooltip'
import TransactionExpenseCategoryCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryCell'
import TransactionExpenseCategoryDropdownCell from 'src/components/TransactionsDataGrid/cells/TransactionExpenseCategoryDropdownCell'
import { getConfiguredColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.config'
import {
  ExpenseCategoryFieldKey,
  TransactionsDataGridRowModel,
} from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { OrganizationRoles } from 'src/enums'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import Crncy from 'src/localization/Crncy'
import Trns, { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import {
  TransactionDateType,
  TransactionFinalApprovalStatus,
  TransactionListModel,
  transactionReviewStatusToColor,
  transactionReviewStatusToString,
} from 'src/models/transaction.models'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  type ExpenseCategoryResponse,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import {
  PostSingleTransactionExpenseCategoryVariables,
  usePostSingleTransactionExpenseCategory,
} from 'src/queries/transactions.queries'
import { useMutationStateTracker } from 'src/queries/useMutationStateTracker'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeCurrencyFromLocale } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectIsBetaOrgNormalizedMerchant,
  selectOrganizationReceiptMgmtEnabledFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

import { chevronColumnDef } from '../DataGrid/cells/ChevronCell'

const colorForTransaction = (
  tx: TransactionListModel,
): undefined | 'highlight' | 'severe' => {
  if (tx?.isDeclined) return 'severe'
  if (tx?.isCredit) return 'highlight'
  return undefined
}

const cellPropsForTransaction = (
  _fieldName: string,
  tx: TransactionListModel,
  bold?: boolean,
  alwaysDefaultColor?: boolean,
) => ({
  color: alwaysDefaultColor ? undefined : colorForTransaction(tx),
  disabled: tx.isPending,
  bold,
})

type UseTransactionsColumnsParams = {
  context?: TransactionReportType
  checkboxMinWidth?: number // e.g. props.checkboxProps ? 115 : 150
  filters: { dateType?: TransactionDateType } // just what we need
  rowApprovalActions?: (row: any) => React.ReactElement
  rowHoverActions?: (row: any) => React.ReactElement
}

type UseTransactionsColumnsResult = {
  columns: Array<GridColDef<TransactionsDataGridRowModel>>
  pinnedLeft: string[]
  pinnedRight: string[]
  ecLoaded: boolean
  orgExpCats: ExpenseCategoryResponse[] | undefined
}

export function useTransactionsColumns(
  params: UseTransactionsColumnsParams,
): UseTransactionsColumnsResult {
  const {
    context,
    checkboxMinWidth = 150,
    filters,
    rowApprovalActions,
    rowHoverActions,
  } = params

  const translate = useTranslate()
  const currency = useSelector(selectNullSafeCurrencyFromLocale)
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const showBulkErpAttachments = useSelector(
    selectFeatureFlaggerFromState,
  ).show_bulk_erp_attachments

  const isClearedAtSelected = filters?.dateType === TransactionDateType.Clear

  const { data: user } = useQueryOwnUser({})
  const { data: orgExpCats, isSuccess: isOrgExpCatsSuccess } =
    useQueryExpenseCategories({
      filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
    })
  const { data: departmentsList } = useQueryDepartments({
    organizationId: user?.organizationId || '',
    includeArchived: true,
  })
  const enableNormalizedMerchant = useSelector(
    selectIsBetaOrgNormalizedMerchant,
  )

  // Build helpers that depend on store data
  const activeTransactionConnection = useCallback(
    (tx: TransactionListModel) =>
      tx?.connectedPlatforms?.find(
        (c) => c.platform === activeIntegration?.connectedPlatform,
      ),
    [activeIntegration?.connectedPlatform],
  )

  const statusToIcon = (reviewStatus: TransactionFinalApprovalStatus) => {
    switch (reviewStatus) {
      case TransactionFinalApprovalStatus.Reviewed:
        return <SvgCheckNew height={14} width={14} />
      case TransactionFinalApprovalStatus.Flagged:
        return <SvgFlagNew height={14} width={14} />
      case TransactionFinalApprovalStatus.PendingReview:
        return <SvgClock stroke={yellow800} height={14} width={14} />
      default:
        return undefined
    }
  }

  const { mutateAsync: postCategoryLabel } =
    usePostSingleTransactionExpenseCategory()

  const { getMutationsFor } =
    useMutationStateTracker<PostSingleTransactionExpenseCategoryVariables>({
      mutationKey: transactionsQueryKeys.postSingleExpenseCategory(),
      status: 'pending',
    })

  /** Build static columns */
  const staticCols = useMemo<Array<GridColDef<TransactionsDataGridRowModel>>>(
    () => [
      {
        field: 'date',
        headerName:
          context === TransactionReportType.AccountingTransactions ||
          context === TransactionReportType.ExpenseReport
            ? translate('Clear Date')
            : translate('Date'),
        groupable: false,
        minWidth: checkboxMinWidth,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx?.isPending && isClearedAtSelected ? (
                  <Trns source="Pending" />
                ) : tx?.isReversal && isClearedAtSelected ? (
                  <Trns source="Reversal" />
                ) : isClearedAtSelected ? (
                  tx?.dateClearedString
                ) : (
                  tx?.dateString
                )
              }
              params={params}
              {...cellPropsForTransaction('date', tx, true, true)}
            />
          )
        },
      },
      {
        field: 'merchant',
        headerName: translate('Merchant'),
        flex: 1,
        minWidth: enableNormalizedMerchant ? 190 : 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          const merchantImageSrc = tx.merchantImageSrc('small')
          return (
            <DataGridCell
              text={
                enableNormalizedMerchant
                  ? tx.normalizedMerchantNameOrUnknown
                  : tx.merchantNameOrUnknown
              }
              subtext={tx.mccGroupIfKnownMerchant}
              icon={
                enableNormalizedMerchant ? (
                  <MerchantIcon
                    size={32}
                    merchantImgSrc={merchantImageSrc}
                    mccGroupKey={tx.mccGroupKey ?? 'OTHER'}
                  />
                ) : undefined
              }
              params={params}
              {...cellPropsForTransaction('merchant', tx, true, true)}
            />
          )
        },
      },
      {
        field: 'originalMerchant',
        headerName: translate('Original Merchant'),
        minWidth: 150,
        renderCell: (params): React.ReactElement => {
          const transaction = params.row.transaction
          return (
            <DataGridCell
              text={transaction.merchantNameOrUnknown}
              subtext={transaction.mccGroupIfKnownMerchant}
              params={params}
              {...cellPropsForTransaction('merchant', transaction, true, true)}
            />
          )
        },
      },
      {
        field: 'card',
        headerName: translate('Card'),
        sortable: false,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <TextAndPillCell
              text={tx.vcnDisplayName}
              pillText={tx.vcnLast4}
              secondPillText={
                !tx.isVirtual ? translate('Physical Card') : undefined
              }
              params={params}
              {...cellPropsForTransaction('card', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'cardUser',
        headerName: translate('Card User'),
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx?.nameOnCard}
              params={params}
              {...cellPropsForTransaction('cardUser', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'team',
        headerName: translate('Team'),
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          const name = departmentsList?.find((d) => d.id === tx.teamId)?.name
          return (
            <DataGridCell
              text={name}
              params={params}
              {...cellPropsForTransaction('team', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'cardholderEmail',
        headerName: translate('Card User Email'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx?.cardholderEmail}
              params={params}
              {...cellPropsForTransaction('cardholderEmail', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'managerReviewStatus',
        headerName: translate('Manager'),
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction

          // Display "--" if there is no managerId or managerReviewId, meaning no manager assigned
          if (
            tx?.managerId === undefined &&
            tx?.managerReviewId === undefined
          ) {
            return (
              <DataGridCell
                params={params}
                text={
                  <div
                    css={css`
                      min-width: 24px;
                      text-align: center;
                      width: 100%;
                    `}
                  >
                    {'--'}
                  </div>
                }
                disabled
              />
            )
          }
          return (
            <DataGridCellWrapper params={params}>
              <ManagerReviewIconCell
                status={tx?.managerReviewStatus}
                rejectionReason={tx?.managerRejectionReason}
              />
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'finalReview',
        headerName: translate('Final Approval'),
        minWidth: 150,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <PillCell
              color={transactionReviewStatusToColor(tx.reviewStatus)}
              params={params}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                {statusToIcon(tx.reviewStatus) !== undefined
                  ? statusToIcon(tx.reviewStatus)
                  : null}
                {translate(transactionReviewStatusToString(tx.reviewStatus))}
              </div>
            </PillCell>
          )
        },
      },
      {
        field: 'amount',
        align: 'right',
        headerName: translate('Amount'),
        minWidth: 100,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.clearingOrAuthLabel(true)}
              subtext={!tx?.isNonCreditCleared && tx.pseudoStatusLabelRowView}
              params={params}
              {...cellPropsForTransaction('amount', tx, false, false)}
            />
          )
        },
      },
      {
        field: 'currency',
        headerName: translate('Currency'),
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        hideable: false,
        headerClassName: 'hideRightSeparator',
        width: 20,
        renderHeader: () => <></>,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authBillingCurrency !== currency && tx.authBillingCurrency
              }
              params={params}
              {...cellPropsForTransaction('currency', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'authBillingAmountCents',
        align: 'right',
        headerName: translate('Billing Amount'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authBillingAmountCents && (
                  <Crncy
                    currency={currency}
                    amount={toDollars(tx.authBillingAmountCents)}
                  />
                )
              }
              params={params}
              {...cellPropsForTransaction(
                'authBillingAmountCents',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authBillingCurrency',
        headerName: translate('Billing Currency'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx?.authBillingCurrency}
              params={params}
              {...cellPropsForTransaction(
                'authBillingCurrency',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authMerchantAmountCents',
        align: 'right',
        headerName: translate('Merchant Amount'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={
                tx.authMerchantAmountCents && (
                  <Crncy
                    currency={currency}
                    amount={toDollars(tx.authMerchantAmountCents)}
                  />
                )
              }
              params={params}
              {...cellPropsForTransaction(
                'authMerchantAmountCents',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authMerchantCurrency',
        headerName: translate('Merchant Currency'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.authMerchantCurrency}
              params={params}
              {...cellPropsForTransaction(
                'authMerchantCurrency',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'authExchangeRate',
        headerName: translate('Exchange Rate'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.authExchangeRate}
              params={params}
              {...cellPropsForTransaction('authExchangeRate', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'attachmentsCount',
        headerName: translate('Number of Attachments'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.attachmentsCount}
              params={params}
              {...cellPropsForTransaction('attachmentsCount', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'creditCardDisplayName',
        headerName: translate('Funding Source'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          return (
            <DataGridCell
              text={tx.creditCardDisplayName}
              params={params}
              {...cellPropsForTransaction(
                'creditCardDisplayName',
                tx,
                false,
                true,
              )}
            />
          )
        },
      },
      {
        field: 'creditCardType',
        headerName: translate('Type of Account'),
        sortable: false,
        renderCell: (params) => {
          const tx = params.row.transaction
          const label =
            tx?.creditCardType === CreditCardType.Delegate
              ? 'Budget'
              : tx?.creditCardType !== CreditCardType.Source
                ? 'Physical Card'
                : ''
          return (
            <DataGridCell
              text={label}
              params={params}
              {...cellPropsForTransaction('creditCardType', tx, false, true)}
            />
          )
        },
      },
      {
        field: 'receipts',
        headerName: translate('Receipts'),
        resizable: false,
        sortable: false,
        minWidth: 100,
        renderCell: (params) => {
          const tx = params.row.transaction
          const hasAny = (tx?.attachmentsCount ?? 0) > 0
          const needs =
            receiptManagementEnabled &&
            tx?.attachmentsCount === 0 &&
            tx?.receiptRequired
          return hasAny || needs ? (
            <DataGridCellWrapper params={params}>
              {hasAny && (
                <ReceiptsTooltip
                  count={tx.attachmentsCount}
                  placement={'top'}
                />
              )}
              {needs && !user?.hasOrgBookkeeperPermissions && (
                <ReceiptsTooltip placement={'top'} isMissing />
              )}
              {needs && user?.hasOrgBookkeeperPermissions && (
                <SingleReceiptReminder
                  transactionId={tx?.id}
                  name={tx?.nameOnCard}
                  wrapText
                  maxWidth={150}
                />
              )}
            </DataGridCellWrapper>
          ) : (
            <></>
          )
        },
      },
      {
        field: 'txnSynced',
        headerName: showBulkErpAttachments
          ? translate('Transactions Synced')
          : translate('Sync Status'),
        sortable: false,
        minWidth: 125,
        renderCell: (params) => {
          const tx = params.row.transaction
          const conn = activeTransactionConnection(tx)
          return (
            <DataGridCellWrapper params={params}>
              {user?.organizationRole !== OrganizationRoles.member && conn && (
                <TransactionPushStatusTooltip
                  placement={'left'}
                  status={conn?.syncStatus}
                  platform={conn?.platform}
                  connectedPlatformName={activeIntegration?.platformName ?? ''}
                />
              )}
            </DataGridCellWrapper>
          )
        },
      },
      ...(showBulkErpAttachments
        ? [
            {
              field: 'receiptsSynced',
              headerName: translate('Receipts Synced'),
              sortable: false,
              minWidth: 125,
              renderCell: (params: any) => {
                const tx = params.row.transaction
                const conn = activeTransactionConnection(tx)
                return (
                  <DataGridCellWrapper params={params}>
                    {user?.organizationRole !== OrganizationRoles.member &&
                      conn &&
                      tx.receiptAttachmentSyncStatus && (
                        <ReceiptPushStatusTooltip
                          openLeft
                          status={tx.receiptAttachmentSyncStatus}
                          platform={conn?.platform}
                        />
                      )}
                  </DataGridCellWrapper>
                )
              },
            },
          ]
        : []),
      {
        field: 'status',
        headerName: translate('Review Status'),
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <PillCell color="pending" params={params}>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <SvgClock stroke={yellow800} height={14} width={14} />
              {translate('Pending Review')}
            </div>
          </PillCell>
        ),
      },
    ],
    [
      translate,
      checkboxMinWidth,
      isClearedAtSelected,
      departmentsList,
      currency,
      receiptManagementEnabled,
      user?.hasOrgBookkeeperPermissions,
      user?.organizationRole,
      activeIntegration?.platformName,
      activeTransactionConnection,
      showBulkErpAttachments,
    ],
  )

  /** Dynamic Expense Category columns */
  const ecCols = useMemo<
    Array<GridColDef<TransactionsDataGridRowModel>>
  >(() => {
    if (!orgExpCats) return []
    const editable = context !== TransactionReportType.ExpenseReport
    return orgExpCats.map((cat) => ({
      field: `ec:${cat.id}` as ExpenseCategoryFieldKey,
      headerName: cat.name,
      sortable: false,
      minWidth: 200,
      editable,
      valueGetter: (_v, row) =>
        row.expenseCategoryCellData[`ec:${cat.id}`]?.labelIdOrFreeText,
      valueFormatter: (_v, row) =>
        row.expenseCategoryCellData[`ec:${cat.id}`]?.valueText,
      renderCell: (params: GridCellParams<TransactionsDataGridRowModel>) => (
        <TransactionExpenseCategoryCell
          params={params}
          onClear={() =>
            postCategoryLabel({
              transactionId: params.row.transaction.id,
              categoryId: cat.id,
              body: { labelId: undefined },
            }).catch(() => {
              /* swallow or surface via toast here if desired */
            })
          }
          loading={
            getMutationsFor({
              categoryId: cat.id,
              transactionId: params.row.transaction.id,
            }).length > 0
          }
          editable={editable}
        />
      ),
      renderEditCell: (params) => (
        <TransactionExpenseCategoryDropdownCell
          params={params}
          editMode="cell"
          category={cat}
          loading={
            getMutationsFor({
              categoryId: cat.id,
              transactionId: params.row.transaction.id,
            }).length > 0
          }
        />
      ),
    }))
  }, [orgExpCats, context, getMutationsFor, postCategoryLabel])

  /** Optional tail cells */
  const tailCols = useMemo<
    Array<GridColDef<TransactionsDataGridRowModel>>
  >(() => {
    const tail: GridColDef<TransactionsDataGridRowModel>[] = []
    if (rowApprovalActions)
      tail.push(approvalActionsCellColDef(translate, rowApprovalActions))
    if (rowHoverActions) tail.push(hoverActionsCellColDef(rowHoverActions))
    tail.push(chevronColumnDef)
    return tail
  }, [rowApprovalActions, rowHoverActions])

  /** Final columns list */
  const columns = React.useMemo(
    () => [...staticCols, ...ecCols, ...tailCols],
    [staticCols, ecCols, tailCols],
  )

  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()

  const configured = useMemo(
    () => getConfiguredColumns(context, columns, managerReviewRulesEnabled),
    [context, columns, managerReviewRulesEnabled],
  )
  const orderedColumns = configured.columns
  const pinnedLeft = configured.pinnedLeft
  const pinnedRight = configured.pinnedRight

  const processedOrderedColumns = useMemo(() => {
    if (!enableNormalizedMerchant)
      return orderedColumns.filter((col) => col.field !== 'originalMerchant')
    return orderedColumns
  }, [enableNormalizedMerchant, orderedColumns])

  return {
    columns: processedOrderedColumns,
    pinnedLeft,
    pinnedRight,
    ecLoaded: isOrgExpCatsSuccess,
    orgExpCats,
  }
}
